// Iter122j · Step 1 · Pagina anagrafica Credius (clienti + controparti esterni)
// CRUD dedicato per i soggetti Credius NON presenti nelle anagrafiche condòmini.
// I "clienti" possono (Step 2) ricevere credenziali di accesso; le "controparti"
// sono solo dati anagrafici usati nelle pratiche/documenti.
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Users, UserPlus, Search, Pencil, Trash2, Mail, Phone, MapPin, ShieldAlert, Scale, Info, KeyRound, RefreshCw, ShieldOff, Copy, CheckCircle2, Bell, BellOff } from "lucide-react";
import { highlightMatch } from "../lib/highlightMatch";
import { CestinoButton } from "../components/CestinoButton";

const EMPTY = {
  nome: "", tipo_persona: "Persona fisica", ruolo_default: "cliente",
  codice_fiscale: "", indirizzo: "", citta: "", cap: "", provincia: "",
  telefono: "", email: "", pec: "", note: "",
  // Iter122af · dati amministratore p.t. quando il soggetto è un Condominio
  amministratore: {
    nome: "", codice_fiscale: "", partita_iva: "",
    indirizzo: "", citta: "", cap: "", provincia: "",
    telefono: "", email: "", pec: "",
  },
  // Iter122k48 · Dati veicolo/assicurativi (opzionali, attivati da flag distinti)
  ha_dati_veicolo: false,
  dati_veicolo: {
    targa: "", modello: "",
    proprietario: { nome: "", codice_fiscale: "", ref_id: "" },
    assicurato: { nome: "", codice_fiscale: "", ref_id: "" },
    conducente: { nome: "", codice_fiscale: "" },
  },
  ha_dati_assicurativi: false,
  dati_assicurativi: { numero_polizza: "", compagnia: "", agenzia: "" },
};

const CLASS_INPUT = "h-11";

function SoggettoDialog({ open, initial, onClose, onSave, anagrafiche = [] }) {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  // Iter122k82 · Ricerca cross-anagrafica (condòmini, fornitori, soggetti Credius)
  const [lookupQ, setLookupQ] = useState("");
  const [lookupRes, setLookupRes] = useState([]);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  // Iter122k86 · Opzione A · avviso quando il soggetto selezionato è già collegato ad un account Ginius
  const [linkedSource, setLinkedSource] = useState(null); // {source, ref_id, nome, hint}
  useEffect(() => { setF(initial || EMPTY); }, [initial, open]);
  useEffect(() => {
    // Reset search state alla chiusura/apertura del dialog
    setLookupQ(""); setLookupRes([]); setLookupOpen(false); setLinkedSource(null);
  }, [open, initial]);
  useEffect(() => {
    // Debounce ricerca
    const q = lookupQ.trim();
    if (q.length < 2) { setLookupRes([]); setLookupOpen(false); return; }
    setLookupBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/credius/anagrafica/lookup`, { params: { q, limit: 15 } });
        setLookupRes(Array.isArray(data) ? data : []);
        setLookupOpen(true);
      } catch (e) {
        setLookupRes([]);
      } finally { setLookupBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [lookupQ]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const applyLookup = (r) => {
    setF((s) => ({
      ...s,
      nome: r.nome || s.nome,
      tipo_persona: r.tipo_persona || s.tipo_persona,
      codice_fiscale: r.codice_fiscale || s.codice_fiscale,
      indirizzo: r.indirizzo || s.indirizzo,
      cap: r.cap || s.cap,
      citta: r.citta || s.citta,
      provincia: r.provincia || s.provincia,
      telefono: r.telefono || s.telefono,
      email: r.email || s.email,
      pec: r.pec || s.pec,
    }));
    setLookupQ(""); setLookupRes([]); setLookupOpen(false);
    // Iter122k86 · Opzione A · se la sorgente è già un utente Ginius (condòmino/fornitore/condominio),
    // avvisa che creare un duplicato Credius rompe il collegamento con l'account.
    if (["condomino", "fornitore", "condominio"].includes(r.source)) {
      setLinkedSource({ source: r.source, ref_id: r.ref_id, nome: r.nome, hint: r.hint });
    } else {
      setLinkedSource(null);
    }
    toast.success(`Dati importati da: ${r.hint || r.source}`);
  };

  const save = async () => {
    if (!f.nome.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = { ...f, nome: f.nome.trim() };
      if (payload.codice_fiscale) payload.codice_fiscale = payload.codice_fiscale.toUpperCase().trim();
      if (payload.email) payload.email = payload.email.toLowerCase().trim();
      await onSave(payload);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="credius-anagrafica-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-900">
            <Scale className="w-5 h-5" />
            {initial?.id ? "Modifica soggetto Credius" : "Nuovo soggetto Credius"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg p-3 bg-indigo-50 border-2 border-indigo-200 text-sm text-indigo-900 flex gap-2" data-testid="credius-anagrafica-info">
            <Info className="w-5 h-5 shrink-0 mt-0.5" />
            <span>
              <b>Clienti</b> = soggetti che la tua difesa/tua Amministrazione tutela (potranno accedere alla app per monitorare le loro pratiche — dallo Step 2).
              <br /><b>Controparti</b> = dati anagrafici usati solo per compilare atti/lettere/PDF (nessun account).
            </span>
          </div>

          {/* Iter122k82 · Ricerca soggetto esistente (condòmini, fornitori, rubrica Credius) */}
          {!initial?.id && (
            <div className="relative" data-testid="credius-anagrafica-lookup-wrap">
              <Label className="text-sm font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1.5">
                <Search className="w-4 h-4" /> Cerca soggetto esistente
              </Label>
              <div className="text-[11px] text-slate-500 mb-1">
                Cerca in <b>condòmini</b>, <b>fornitori</b> dello studio e <b>anagrafica Credius</b> per pre-compilare i dati senza doppie digitazioni.
              </div>
              <div className="relative">
                <Input data-testid="credius-anagrafica-lookup-input"
                  value={lookupQ} onChange={(e) => setLookupQ(e.target.value)}
                  onFocus={() => lookupRes.length && setLookupOpen(true)}
                  placeholder="Nome, codice fiscale, città, email…"
                  className={`${CLASS_INPUT} pl-9`} />
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                {lookupBusy && <span className="text-[10px] text-slate-400 absolute right-3 top-3">…</span>}
              </div>
              {lookupOpen && lookupRes.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border-2 border-slate-200 bg-white shadow-lg" data-testid="credius-anagrafica-lookup-results">
                  {lookupRes.map((r, i) => {
                    const badge = r.source === "condominio" ? "bg-sky-100 text-sky-800 border-sky-200"
                                  : r.source === "condomino" ? "bg-purple-100 text-purple-800 border-purple-200"
                                  : r.source === "fornitore" ? "bg-amber-100 text-amber-800 border-amber-200"
                                  : "bg-emerald-100 text-emerald-800 border-emerald-200";
                    const label = r.source === "condominio" ? "Condominio"
                                  : r.source === "condomino" ? "Condòmino"
                                  : r.source === "fornitore" ? "Fornitore"
                                  : "Rubrica Credius";
                    return (
                      <button key={`${r.source}-${r.ref_id}-${i}`} type="button"
                        onClick={() => applyLookup(r)}
                        data-testid={`credius-anagrafica-lookup-item-${i}`}
                        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-indigo-50 border-b border-slate-100 last:border-b-0">
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${badge}`}>{label}</span>
                        <span className="flex-1 min-w-0">
                          <div className="font-bold text-slate-800 truncate">{r.nome}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {[r.codice_fiscale, r.citta, r.email].filter(Boolean).join(" · ") || r.hint}
                          </div>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {lookupOpen && !lookupBusy && lookupQ.trim().length >= 2 && lookupRes.length === 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-xs text-slate-500" data-testid="credius-anagrafica-lookup-empty">
                  Nessun soggetto trovato. Compila manualmente il form qui sotto.
                </div>
              )}
            </div>
          )}

          {/* Iter122k86 · Opzione A · avviso quando il soggetto è già collegato ad un account Ginius */}
          {!initial?.id && linkedSource && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="credius-anagrafica-linked-warning">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 shrink-0 mt-0.5 text-amber-700" />
                <div className="flex-1">
                  <div className="font-bold mb-1">
                    "{linkedSource.nome}" è già presente in Ginius come <span className="uppercase">{linkedSource.source === "condominio" ? "Condominio" : linkedSource.source === "condomino" ? "Condòmino" : "Fornitore"}</span>.
                  </div>
                  <div className="text-xs leading-relaxed">
                    <b>Non serve duplicarlo qui.</b> Vai alla scheda <b>"Nuova pratica"</b> e selezionalo direttamente dal menù {linkedSource.source === "condominio" ? "«Condominio»" : linkedSource.source === "condomino" ? "«Condòmino»" : "«Fornitore Studio»"}: se il suo account Ginius è attivo, vedrà la pratica automaticamente dal proprio portale. <br />
                    Se invece crei un record Credius separato, il collegamento con l'account non verrà mantenuto.
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <a href="/credius/nuova" data-testid="credius-anagrafica-linked-nuova-pratica"
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-md bg-indigo-900 text-white hover:bg-indigo-950">
                      Vai a Nuova pratica
                    </a>
                    <button type="button" data-testid="credius-anagrafica-linked-dismiss"
                      onClick={() => setLinkedSource(null)}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-md border-2 border-amber-400 text-amber-800 hover:bg-amber-100">
                      Crea comunque record separato
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-bold uppercase tracking-wide text-slate-600">Ruolo predefinito *</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button type="button" data-testid="credius-anagrafica-ruolo-cliente"
                onClick={() => set("ruolo_default", "cliente")}
                className={`h-11 rounded-lg border-2 font-bold flex items-center justify-center gap-2 transition-colors ${f.ruolo_default === "cliente" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white text-slate-500 hover:border-emerald-300"}`}>
                <Scale className="w-4 h-4" /> Cliente
              </button>
              <button type="button" data-testid="credius-anagrafica-ruolo-controparte"
                onClick={() => set("ruolo_default", "controparte")}
                className={`h-11 rounded-lg border-2 font-bold flex items-center justify-center gap-2 transition-colors ${f.ruolo_default === "controparte" ? "border-rose-500 bg-rose-50 text-rose-800" : "border-slate-300 bg-white text-slate-500 hover:border-rose-300"}`}>
                <ShieldAlert className="w-4 h-4" /> Controparte
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Puoi comunque usare qualsiasi soggetto come cliente o controparte nella singola pratica.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Nome / Ragione sociale *</Label>
              <Input data-testid="credius-anagrafica-nome" className={CLASS_INPUT} value={f.nome} onChange={(e) => set("nome", e.target.value)} />
            </div>
            <div>
              <Label>Tipo</Label>
              <select className={`${CLASS_INPUT} w-full border-2 border-slate-300 rounded-md px-2`} data-testid="credius-anagrafica-tipo"
                value={f.tipo_persona} onChange={(e) => set("tipo_persona", e.target.value)}>
                <option>Persona fisica</option>
                <option>Persona giuridica</option>
                <option>Ente pubblico</option>
                <option>Condominio</option>
              </select>
            </div>
            <div>
              <Label>Codice fiscale / P.IVA</Label>
              <Input data-testid="credius-anagrafica-cf" className={CLASS_INPUT} value={f.codice_fiscale}
                onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} />
            </div>
            <div className="sm:col-span-2">
              <Label>Indirizzo</Label>
              <Input data-testid="credius-anagrafica-indirizzo" className={CLASS_INPUT} value={f.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} />
            </div>
            <div><Label>CAP</Label><Input className={CLASS_INPUT} value={f.cap} onChange={(e) => set("cap", e.target.value)} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label>Città</Label><Input className={CLASS_INPUT} value={f.citta} onChange={(e) => set("citta", e.target.value)} /></div>
              <div><Label>Prov.</Label><Input className={CLASS_INPUT} maxLength={2} value={f.provincia} onChange={(e) => set("provincia", e.target.value.toUpperCase())} /></div>
            </div>
            <div><Label>Telefono</Label><Input className={CLASS_INPUT} value={f.telefono} onChange={(e) => set("telefono", e.target.value)} /></div>
            <div>
              <Label>Email {f.ruolo_default === "cliente" && <span className="text-xs text-emerald-700 font-semibold">(richiesta per rilasciare l&apos;account)</span>}</Label>
              <Input data-testid="credius-anagrafica-email" className={CLASS_INPUT} type="email" value={f.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="sm:col-span-2"><Label>PEC</Label><Input className={CLASS_INPUT} value={f.pec} onChange={(e) => set("pec", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Note</Label><Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} /></div>
          </div>

          {/* Iter122af · dati amministratore p.t. quando il tipo è "Condominio" */}
          {f.tipo_persona === "Condominio" && (
            <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-3 mt-2" data-testid="credius-anagrafica-amministratore">
              <div className="text-sm font-bold text-indigo-900 mb-2">Amministratore pro tempore</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Nome / Ragione sociale</Label>
                  <Input data-testid="credius-amm-nome" className={CLASS_INPUT}
                    value={f.amministratore?.nome || ""}
                    onChange={(e) => set("amministratore", { ...(f.amministratore || {}), nome: e.target.value })} />
                </div>
                <div>
                  <Label>Codice fiscale</Label>
                  <Input className={CLASS_INPUT}
                    value={f.amministratore?.codice_fiscale || ""}
                    onChange={(e) => set("amministratore", { ...(f.amministratore || {}), codice_fiscale: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input className={CLASS_INPUT}
                    value={f.amministratore?.partita_iva || ""}
                    onChange={(e) => set("amministratore", { ...(f.amministratore || {}), partita_iva: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Indirizzo studio</Label>
                  <Input className={CLASS_INPUT}
                    value={f.amministratore?.indirizzo || ""}
                    onChange={(e) => set("amministratore", { ...(f.amministratore || {}), indirizzo: e.target.value })} />
                </div>
                <div><Label>CAP</Label><Input className={CLASS_INPUT}
                  value={f.amministratore?.cap || ""}
                  onChange={(e) => set("amministratore", { ...(f.amministratore || {}), cap: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2"><Label>Città</Label><Input className={CLASS_INPUT}
                    value={f.amministratore?.citta || ""}
                    onChange={(e) => set("amministratore", { ...(f.amministratore || {}), citta: e.target.value })} /></div>
                  <div><Label>Prov.</Label><Input className={CLASS_INPUT} maxLength={2}
                    value={f.amministratore?.provincia || ""}
                    onChange={(e) => set("amministratore", { ...(f.amministratore || {}), provincia: e.target.value.toUpperCase() })} /></div>
                </div>
                <div><Label>Telefono</Label><Input className={CLASS_INPUT}
                  value={f.amministratore?.telefono || ""}
                  onChange={(e) => set("amministratore", { ...(f.amministratore || {}), telefono: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" className={CLASS_INPUT}
                  value={f.amministratore?.email || ""}
                  onChange={(e) => set("amministratore", { ...(f.amministratore || {}), email: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>PEC</Label><Input className={CLASS_INPUT}
                  value={f.amministratore?.pec || ""}
                  onChange={(e) => set("amministratore", { ...(f.amministratore || {}), pec: e.target.value })} /></div>
              </div>
              <p className="text-xs text-indigo-700 mt-2">Questi dati verranno usati nella corrispondenza e nei PDF quando il soggetto è un condominio.</p>
            </div>
          )}

          {/* Iter122k48 · Dati veicolo (opzionale) — utile per pratiche di sinistro auto */}
          <div className="rounded-lg border-2 border-slate-200 p-3 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="credius-anagrafica-flag-veicolo"
                checked={!!f.ha_dati_veicolo}
                onChange={(e) => set("ha_dati_veicolo", e.target.checked)} />
              <span className="text-sm font-bold text-slate-800">Dati veicolo</span>
              <span className="text-xs text-slate-500">(sinistri auto)</span>
            </label>
            {f.ha_dati_veicolo && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="credius-anagrafica-veicolo-fields">
                <div><Label>Targa</Label>
                  <Input data-testid="veicolo-targa" className={CLASS_INPUT}
                    value={f.dati_veicolo?.targa || ""}
                    onChange={(e) => set("dati_veicolo", { ...(f.dati_veicolo || {}), targa: e.target.value.toUpperCase() })} /></div>
                <div><Label>Modello</Label>
                  <Input data-testid="veicolo-modello" className={CLASS_INPUT}
                    value={f.dati_veicolo?.modello || ""}
                    onChange={(e) => set("dati_veicolo", { ...(f.dati_veicolo || {}), modello: e.target.value })} /></div>
                {["proprietario", "assicurato"].map((key) => (
                  <div key={key} className="sm:col-span-2 rounded border border-slate-200 p-2 bg-slate-50/60">
                    <Label className="text-xs font-bold uppercase text-slate-600">{key === "proprietario" ? "Proprietario" : "Assicurato"}</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                      <select className="h-10 border-2 border-slate-300 rounded px-2 text-sm bg-white"
                        data-testid={`veicolo-${key}-picker`}
                        value={f.dati_veicolo?.[key]?.ref_id || ""}
                        onChange={(e) => {
                          const rid = e.target.value;
                          if (!rid) { set("dati_veicolo", { ...(f.dati_veicolo || {}), [key]: { nome: "", codice_fiscale: "", ref_id: "" } }); return; }
                          const src = anagrafiche.find((a) => a.id === rid);
                          if (!src) return;
                          set("dati_veicolo", { ...(f.dati_veicolo || {}), [key]: { nome: src.nome || "", codice_fiscale: src.codice_fiscale || "", ref_id: rid } });
                        }}>
                        <option value="">— Richiama da anagrafica…</option>
                        {anagrafiche.filter((a) => a.id !== initial?.id).map((a) => (
                          <option key={a.id} value={a.id}>{a.nome}{a.codice_fiscale ? ` · ${a.codice_fiscale}` : ""}</option>
                        ))}
                      </select>
                      <Input data-testid={`veicolo-${key}-nome`} placeholder="Nome (nuovo)" className="h-10"
                        value={f.dati_veicolo?.[key]?.nome || ""}
                        onChange={(e) => set("dati_veicolo", { ...(f.dati_veicolo || {}), [key]: { ...(f.dati_veicolo?.[key] || {}), nome: e.target.value, ref_id: "" } })} />
                      <Input placeholder="Codice fiscale" className="h-10 sm:col-span-2 font-mono"
                        value={f.dati_veicolo?.[key]?.codice_fiscale || ""}
                        onChange={(e) => set("dati_veicolo", { ...(f.dati_veicolo || {}), [key]: { ...(f.dati_veicolo?.[key] || {}), codice_fiscale: e.target.value.toUpperCase(), ref_id: "" } })} />
                    </div>
                  </div>
                ))}
                <div className="sm:col-span-2 rounded border border-slate-200 p-2 bg-slate-50/60">
                  <Label className="text-xs font-bold uppercase text-slate-600">Conducente (se diverso da proprietario/assicurato)</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                    <Input data-testid="veicolo-conducente-nome" placeholder="Nome" className="h-10"
                      value={f.dati_veicolo?.conducente?.nome || ""}
                      onChange={(e) => set("dati_veicolo", { ...(f.dati_veicolo || {}), conducente: { ...(f.dati_veicolo?.conducente || {}), nome: e.target.value } })} />
                    <Input placeholder="Codice fiscale" className="h-10 font-mono"
                      value={f.dati_veicolo?.conducente?.codice_fiscale || ""}
                      onChange={(e) => set("dati_veicolo", { ...(f.dati_veicolo || {}), conducente: { ...(f.dati_veicolo?.conducente || {}), codice_fiscale: e.target.value.toUpperCase() } })} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Iter122k48 · Dati assicurativi (opzionale) — utile per manleva/tutela legale */}
          <div className="rounded-lg border-2 border-slate-200 p-3 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="credius-anagrafica-flag-assicurativi"
                checked={!!f.ha_dati_assicurativi}
                onChange={(e) => set("ha_dati_assicurativi", e.target.checked)} />
              <span className="text-sm font-bold text-slate-800">Dati assicurativi</span>
              <span className="text-xs text-slate-500">(polizza / agenzia / compagnia)</span>
            </label>
            {f.ha_dati_assicurativi && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="credius-anagrafica-assicurativi-fields">
                <div>
                  <Label>Numero polizza</Label>
                  <Input data-testid="assic-numero-polizza" className={CLASS_INPUT}
                    value={f.dati_assicurativi?.numero_polizza || ""}
                    onChange={(e) => set("dati_assicurativi", { ...(f.dati_assicurativi || {}), numero_polizza: e.target.value })} />
                </div>
                <div>
                  <Label>Compagnia assicurativa</Label>
                  <Input data-testid="assic-compagnia" className={CLASS_INPUT}
                    value={f.dati_assicurativi?.compagnia || ""}
                    onChange={(e) => set("dati_assicurativi", { ...(f.dati_assicurativi || {}), compagnia: e.target.value })} />
                </div>
                <div>
                  <Label>Agenzia</Label>
                  <Input data-testid="assic-agenzia" className={CLASS_INPUT}
                    value={f.dati_assicurativi?.agenzia || ""}
                    onChange={(e) => set("dati_assicurativi", { ...(f.dati_assicurativi || {}), agenzia: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="credius-anagrafica-salva" className="bg-indigo-800 hover:bg-indigo-900 text-white" onClick={save} disabled={busy}>
            {busy ? "Salvataggio…" : "Salva soggetto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CrediusAnagrafica() {
  const [tab, setTab] = useState("cliente"); // cliente | controparte
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [credDialog, setCredDialog] = useState({ open: false, data: null, nome: "" });

  // Iter122i · scroll a inizio pagina al cambio tab.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/credius/soggetti?ruolo=${tab}`);
      setList(data || []);
    } catch (e) {
      toast.error("Impossibile caricare l'elenco");
      setList([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((x) =>
      [x.nome, x.codice_fiscale, x.email, x.citta].filter(Boolean).join(" ").toLowerCase().includes(s)
    );
  }, [list, q]);

  const salva = async (payload) => {
    if (payload.id) {
      const { id, ...body } = payload;
      await api.put(`/credius/soggetti/${id}`, body);
      toast.success("Soggetto aggiornato");
    } else {
      await api.post("/credius/soggetti", payload);
      toast.success("Soggetto creato");
    }
    await load();
  };

  const cestina = async (s) => {
    if (!window.confirm(`Spostare "${s.nome}" nel cestino?`)) return;
    try {
      await api.delete(`/credius/soggetti/${s.id}`);
      toast.success("Soggetto spostato nel cestino");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  // Iter122k · Step 2 · account management
  const rilascia = async (s) => {
    if (!s.email) { toast.error("Aggiungi prima un'email al soggetto"); return; }
    if (!window.confirm(`Creare l'account per "${s.nome}" e inviare l'email di attivazione a ${s.email}?`)) return;
    try {
      const { data } = await api.post(`/credius/soggetti/${s.id}/rilascia-credenziali`);
      toast.success(data.email_inviata ? "Credenziali inviate via email" : "Credenziali generate (invio email disattivato)");
      setCredDialog({ open: true, data, nome: s.nome });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const riemetti = async (s) => {
    if (!window.confirm(`Riemettere le credenziali per "${s.nome}"? Verrà generato un nuovo codice e azzerata l'attuale password.`)) return;
    try {
      const { data } = await api.post(`/credius/soggetti/${s.id}/riemetti-credenziali`);
      toast.success(data.email_inviata ? "Nuove credenziali inviate via email" : "Nuove credenziali generate");
      setCredDialog({ open: true, data, nome: s.nome });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const disattiva = async (s) => {
    if (!window.confirm(`Disattivare l'account di "${s.nome}"? Il soggetto non potrà più accedere fino a nuova attivazione.`)) return;
    try {
      await api.post(`/credius/soggetti/${s.id}/disattiva`);
      toast.success("Account disattivato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const mostraLink = async (s) => {
    try {
      const { data } = await api.post(`/credius/soggetti/${s.id}/attivazione-link`);
      setCredDialog({ open: true, data, nome: s.nome });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  // Iter122k64 · Toggle sospensione notifiche cliente (email + push in-app)
  const toggleNotifiche = async (s) => {
    try {
      const { data } = await api.patch(`/credius/soggetti/${s.id}/notifiche`);
      toast.success(data.notifiche_sospese
        ? `Notifiche sospese per "${data.nome}"`
        : `Notifiche riattivate per "${data.nome}"`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  // Iter122ah · account management per l'amministratore p.t. di un condominio esterno
  const rilasciaAmm = async (s) => {
    const amm = s.amministratore || {};
    if (!amm.email) { toast.error("Aggiungi prima un'email all'amministratore p.t."); return; }
    if (!amm.nome) { toast.error("Aggiungi prima il nome dell'amministratore p.t."); return; }
    if (!window.confirm(`Creare l'account per l'amministratore "${amm.nome}" e inviare l'email di attivazione a ${amm.email}?`)) return;
    try {
      const { data } = await api.post(`/credius/soggetti/${s.id}/amministratore/rilascia-credenziali`);
      toast.success(data.email_inviata ? "Credenziali amministratore inviate via email" : "Credenziali amministratore generate (invio email disattivato)");
      setCredDialog({ open: true, data, nome: amm.nome });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const riemettiAmm = async (s) => {
    const amm = s.amministratore || {};
    if (!window.confirm(`Riemettere le credenziali per l'amministratore "${amm.nome || s.nome}"? Verrà generato un nuovo codice e azzerata l'attuale password.`)) return;
    try {
      const { data } = await api.post(`/credius/soggetti/${s.id}/amministratore/riemetti-credenziali`);
      toast.success(data.email_inviata ? "Nuove credenziali amministratore inviate via email" : "Nuove credenziali amministratore generate");
      setCredDialog({ open: true, data, nome: amm.nome || s.nome });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const disattivaAmm = async (s) => {
    const amm = s.amministratore || {};
    if (!window.confirm(`Disattivare l'account dell'amministratore "${amm.nome || s.nome}"?`)) return;
    try {
      await api.post(`/credius/soggetti/${s.id}/amministratore/disattiva`);
      toast.success("Account amministratore disattivato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const mostraLinkAmm = async (s) => {
    const amm = s.amministratore || {};
    try {
      const { data } = await api.post(`/credius/soggetti/${s.id}/amministratore/attivazione-link`);
      setCredDialog({ open: true, data, nome: amm.nome || s.nome });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div data-testid="credius-anagrafica-page" className="max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <div className="w-12 h-12 rounded-2xl bg-indigo-900 flex items-center justify-center shrink-0">
          <Users className="w-7 h-7 text-[#D4AF5A]" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-indigo-950">Credius · Anagrafiche</h1>
          <p className="text-sm text-slate-500">Soggetti esterni utilizzabili nelle pratiche legali (clienti e controparti non presenti nelle anagrafiche condòmini).</p>
        </div>
        <Button data-testid="credius-anagrafica-nuovo" onClick={() => setDialog({ open: true, initial: { ...EMPTY, ruolo_default: tab } })}
          className="bg-indigo-800 hover:bg-indigo-900 text-white">
          <UserPlus className="w-4 h-4 mr-1" /> Nuovo {tab === "cliente" ? "cliente" : "controparte"}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b-2 border-slate-200 mt-6 mb-4">
        <button data-testid="credius-anagrafica-tab-cliente" onClick={() => setTab("cliente")}
          className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${tab === "cliente" ? "bg-emerald-50 border-emerald-500 text-emerald-800" : "border-transparent text-slate-500 hover:text-emerald-700 hover:bg-emerald-50/40"}`}>
          <Scale className="w-4 h-4" /> Clienti
        </button>
        <button data-testid="credius-anagrafica-tab-controparte" onClick={() => setTab("controparte")}
          className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${tab === "controparte" ? "bg-rose-50 border-rose-500 text-rose-800" : "border-transparent text-slate-500 hover:text-rose-700 hover:bg-rose-50/40"}`}>
          <ShieldAlert className="w-4 h-4" /> Controparti
        </button>
        <div className="ml-auto"><CestinoButton entity="credius_soggetti" onChange={load} /></div>
      </div>

      <div className="relative mb-4">
        <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="credius-anagrafica-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome, CF, email o città..." className="h-12 pl-10" />
      </div>

      {loading ? (
        <Card className="p-8 text-center text-slate-500">Caricamento…</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="credius-anagrafica-empty">
          <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          {q ? "Nessun risultato per la ricerca." : (
            tab === "cliente"
              ? "Nessun cliente Credius. Clicca “Nuovo cliente” per iniziare."
              : "Nessuna controparte registrata."
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => (
            <Card key={s.id} data-testid={`credius-anagrafica-row-${s.id}`}
              className="p-3 border-2 hover:border-indigo-400 transition-colors">
              <div className="flex items-start gap-3">
                <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${tab === "cliente" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                  {(s.nome || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-slate-900">{highlightMatch(s.nome, q)}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${tab === "cliente" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-rose-100 text-rose-800 border-rose-300"}`}>
                      {tab === "cliente" ? "CLIENTE" : "CONTROPARTE"}
                    </span>
                    {/* Iter122k88 · badge "Rubrica Ginius" per soggetti linkati direttamente (opzione A) */}
                    {s.linked && (
                      <span data-testid={`credius-anagrafica-linked-badge-${s.id}`}
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${s.origin === "fornitore" ? "bg-indigo-100 text-indigo-800 border-indigo-300" : "bg-sky-100 text-sky-800 border-sky-300"}`}
                        title={s.origin === "fornitore" ? "Fornitore Ginius collegato ad una pratica Credius" : "Condòmino Ginius collegato ad una pratica Credius"}>
                        RUBRICA GINIUS · {s.origin === "fornitore" ? "FORNITORE" : "CONDÒMINO"}
                      </span>
                    )}
                    {/* Iter122k109 · Fornitore Ginius NON ancora usato in una pratica Credius */}
                    {!s.linked && s.origin === "fornitore" && (
                      <span data-testid={`credius-anagrafica-fornitore-disponibile-${s.id}`}
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-300"
                        title="Fornitore anagrafato in Ginius, ancora non usato in nessuna pratica Credius">
                        FORNITORE · DISPONIBILE
                      </span>
                    )}
                    <span className="text-xs text-slate-400">· {s.tipo_persona}</span>
                    {tab === "cliente" && s.account && (
                      s.account.stato === "attivo" ? (
                        <span data-testid={`credius-account-stato-${s.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                          <CheckCircle2 className="w-3 h-3" /> ACCOUNT ATTIVO
                        </span>
                      ) : s.account.stato === "da_attivare" ? (
                        <span data-testid={`credius-account-stato-${s.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                          <KeyRound className="w-3 h-3" /> DA ATTIVARE
                        </span>
                      ) : s.account.stato === "disabilitato" ? (
                        <span data-testid={`credius-account-stato-${s.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-500 text-white">
                          <ShieldOff className="w-3 h-3" /> DISABILITATO
                        </span>
                      ) : null
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                    {s.codice_fiscale && <span className="font-mono">{highlightMatch(s.codice_fiscale, q)}</span>}
                    {s.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {highlightMatch(s.email, q)}</span>}
                    {s.telefono && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {s.telefono}</span>}
                    {(s.citta || s.provincia) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {highlightMatch([s.citta, s.provincia].filter(Boolean).join(" "), q)}</span>}
                    {tab === "cliente" && s.account?.login_id && <span className="inline-flex items-center gap-1 font-mono text-indigo-700">ID: {s.account.login_id}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Iter122k64 · Toggle sospensione notifiche cliente (email + push in-app) */}
                  {tab === "cliente" && !s.linked && (
                    <button type="button"
                      data-testid={`credius-notifiche-toggle-${s.id}`}
                      onClick={() => toggleNotifiche(s)}
                      title={s.notifiche_sospese ? "Notifiche sospese — clicca per riattivare" : "Notifiche attive — clicca per sospendere"}
                      aria-pressed={!s.notifiche_sospese}
                      className={`relative inline-flex items-center h-6 w-11 rounded-full border-2 transition-colors shrink-0 ${
                        s.notifiche_sospese
                          ? "bg-red-500 border-red-600"
                          : "bg-emerald-500 border-emerald-600"
                      }`}>
                      <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        s.notifiche_sospese ? "translate-x-0" : "translate-x-5"
                      } flex items-center justify-center`}>
                        {s.notifiche_sospese
                          ? <BellOff className="w-2.5 h-2.5 text-red-600" />
                          : <Bell className="w-2.5 h-2.5 text-emerald-600" />}
                      </span>
                    </button>
                  )}
                  {tab === "cliente" && !s.linked && (
                    s.account?.esiste ? (
                      <>
                        {s.account.stato === "da_attivare" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-700"
                            data-testid={`credius-anagrafica-link-${s.id}`}
                            onClick={() => mostraLink(s)} title="Mostra/Copia link e codice di attivazione">
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-700"
                          data-testid={`credius-anagrafica-riemetti-${s.id}`}
                          onClick={() => riemetti(s)} title="Riemetti credenziali (nuovo codice)">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        {s.account.stato === "attivo" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600"
                            data-testid={`credius-anagrafica-disattiva-${s.id}`}
                            onClick={() => disattiva(s)} title="Disattiva account">
                            <ShieldOff className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-700"
                        data-testid={`credius-anagrafica-rilascia-${s.id}`}
                        onClick={() => rilascia(s)} title="Rilascia credenziali e invia email di attivazione">
                        <KeyRound className="w-4 h-4" />
                      </Button>
                    )
                  )}
                  {/* Iter122k88 · Le voci "linked" (rubrica Ginius) sono in sola lettura in Credius:
                      la modifica avviene in /residenti o /fornitori.
                      Iter122k109 · Anche i fornitori disponibili non-linkati sono readonly qui. */}
                  {(s.linked || s.readonly) ? (
                    <a
                      href={s.origin === "fornitore" ? `/fornitori?edit=${s.id}` : `/residenti?edit=${s.id}`}
                      data-testid={`credius-anagrafica-linked-go-${s.id}`}
                      title={s.origin === "fornitore" ? "Apri la scheda Fornitore in Rubrica Ginius" : "Apri la scheda Condòmino in Rubrica Ginius"}
                      className="h-8 px-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-indigo-700 hover:bg-indigo-50 rounded-md border border-indigo-300">
                      Vai a Rubrica
                    </a>
                  ) : (
                    <>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-indigo-700"
                        data-testid={`credius-anagrafica-edit-${s.id}`}
                        onClick={() => setDialog({ open: true, initial: s })} title="Modifica">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600"
                        data-testid={`credius-anagrafica-del-${s.id}`}
                        onClick={() => cestina(s)} title="Sposta nel cestino">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {/* Iter122ah · sotto-riga amministratore p.t. per soggetti Condominio (solo tab cliente) */}
              {tab === "cliente" && s.tipo_persona === "Condominio" && (
                <div className="mt-2 ml-13 pl-3 border-l-2 border-indigo-200 flex flex-wrap items-center gap-2" data-testid={`credius-anagrafica-amm-row-${s.id}`}>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-700 shrink-0">Amministratore p.t.</span>
                  {s.amministratore?.nome ? (
                    <div className="flex-1 min-w-0 text-xs text-slate-700 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span className="font-semibold text-slate-900">{s.amministratore.nome}</span>
                      {s.amministratore.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {s.amministratore.email}</span>}
                      {s.amministratore.telefono && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {s.amministratore.telefono}</span>}
                      {s.amministratore.account?.esiste && (
                        s.amministratore.account.stato === "attivo" ? (
                          <span data-testid={`credius-amm-account-stato-${s.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                            <CheckCircle2 className="w-3 h-3" /> ACCOUNT ATTIVO
                          </span>
                        ) : s.amministratore.account.stato === "da_attivare" ? (
                          <span data-testid={`credius-amm-account-stato-${s.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                            <KeyRound className="w-3 h-3" /> DA ATTIVARE
                          </span>
                        ) : s.amministratore.account.stato === "disabilitato" ? (
                          <span data-testid={`credius-amm-account-stato-${s.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-500 text-white">
                            <ShieldOff className="w-3 h-3" /> DISABILITATO
                          </span>
                        ) : null
                      )}
                      {s.amministratore.account?.login_id && <span className="inline-flex items-center gap-1 font-mono text-indigo-700">ID: {s.amministratore.account.login_id}</span>}
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0 text-xs text-slate-400 italic">Nessun amministratore compilato — usa &quot;Modifica&quot; per aggiungerlo.</div>
                  )}
                  <div className="flex items-center gap-1 shrink-0">
                    {s.amministratore?.account?.esiste ? (
                      <>
                        {s.amministratore.account.stato === "da_attivare" && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-700"
                            data-testid={`credius-amm-link-${s.id}`}
                            onClick={() => mostraLinkAmm(s)} title="Mostra/Copia link e codice di attivazione">
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-700"
                          data-testid={`credius-amm-riemetti-${s.id}`}
                          onClick={() => riemettiAmm(s)} title="Riemetti credenziali amministratore">
                          <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                        {s.amministratore.account.stato === "attivo" && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-600"
                            data-testid={`credius-amm-disattiva-${s.id}`}
                            onClick={() => disattivaAmm(s)} title="Disattiva account amministratore">
                            <ShieldOff className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </>
                    ) : (
                      s.amministratore?.email && s.amministratore?.nome && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-700"
                          data-testid={`credius-amm-rilascia-${s.id}`}
                          onClick={() => rilasciaAmm(s)} title="Rilascia credenziali all'amministratore p.t.">
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                      )
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <SoggettoDialog open={dialog.open} initial={dialog.initial}
        onClose={() => setDialog({ open: false, initial: null })}
        onSave={salva}
        anagrafiche={list} />

      {/* Iter122k · Step 2 · dialog credenziali (rilascia/riemetti/mostra-link) */}
      <CredentialsDialog dialog={credDialog} onClose={() => setCredDialog({ open: false, data: null, nome: "" })} />
    </div>
  );
}

function CredentialsDialog({ dialog, onClose }) {
  const d = dialog.data;
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare");
    }
  };
  const messaggioWA = d ? `Ciao ${dialog.nome},\nabbiamo attivato il tuo accesso all'app dello Studio per monitorare la tua pratica legale.\n\nID: ${d.login_id}\nCodice di attivazione: ${d.activation_code}\nLink di attivazione: ${d.link}\n\nAll'atto dell'attivazione ti verrà chiesto di impostare una tua password.` : "";
  return (
    <Dialog open={dialog.open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="credius-anagrafica-cred-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-900">
            <KeyRound className="w-5 h-5" /> Credenziali di accesso — {dialog.nome}
          </DialogTitle>
        </DialogHeader>
        {d && (
          <div className="space-y-3">
            <div className="rounded-lg p-3 bg-emerald-50 border-2 border-emerald-200 text-sm text-emerald-900">
              L&apos;email è stata {d.email_inviata === false ? <>generata (ma <b>NON inviata</b>: puoi copiare il link sotto e condividerlo via WhatsApp o telefono)</> : <><b>inviata</b> a <b>{d.email}</b></>}.
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div className="rounded border-2 border-slate-200 p-2.5">
                <div className="text-[10px] uppercase font-bold text-slate-500">ID di accesso</div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="font-mono text-base font-bold text-indigo-900" data-testid="credius-cred-login-id">{d.login_id}</span>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => copy(d.login_id)}><Copy className="w-3 h-3 mr-1" /> Copia</Button>
                </div>
              </div>
              <div className="rounded border-2 border-slate-200 p-2.5">
                <div className="text-[10px] uppercase font-bold text-slate-500">Codice di attivazione</div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="font-mono text-base font-bold text-amber-700" data-testid="credius-cred-code">{d.activation_code}</span>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => copy(d.activation_code)}><Copy className="w-3 h-3 mr-1" /> Copia</Button>
                </div>
              </div>
              <div className="rounded border-2 border-slate-200 p-2.5">
                <div className="text-[10px] uppercase font-bold text-slate-500">Link di attivazione</div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <a href={d.link} target="_blank" rel="noreferrer" className="text-sm text-indigo-700 underline break-all" data-testid="credius-cred-link">{d.link}</a>
                  <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => copy(d.link)}><Copy className="w-3 h-3 mr-1" /> Copia</Button>
                </div>
              </div>
            </div>
            <div className="rounded-lg p-3 bg-slate-50 border-2 border-slate-200">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase font-bold text-slate-500">Messaggio per WhatsApp / Telefono</div>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="credius-cred-copy-msg" onClick={() => copy(messaggioWA)}>
                  <Copy className="w-3 h-3 mr-1" /> Copia messaggio
                </Button>
              </div>
              <pre className="whitespace-pre-wrap text-xs text-slate-700 font-sans">{messaggioWA}</pre>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="credius-cred-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
